import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  forwardRef,
  Inject,
  Injector,
  Input,
  LOCALE_ID,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { OrderformFascade } from '../../../../fascades/orderform.fascade';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { map, take } from 'rxjs/operators';
import { BehaviorSubject, firstValueFrom, Observable, of, catchError } from 'rxjs';
import { AbstractMethodComponent } from '../abstract-method.component';
import {
  CustomFormData,
  InterfacePaymentProviderStipe,
  OrderformOrderState,
  OrderformSettingsState,
  OrderFormValidationResult,
  PAYMENT_ERROR_MESSAGES,
} from '@orderform/widgets';
import { CREDIT_CARD_DEFAULT_STYLES, CreditCardStyles } from '../../payment-methods.model';
import { HttpClient } from '@angular/common/http';
import { OrderformService } from '../../../../services/orderform.service';
import { ExternalResourcesLoaderService } from '../../../../services/external-resources-loader.service';
import { Transaction } from '@elastic/apm-rum';
import { CustomErrorMessageService } from '../../services/custom-error-messages.service';

interface StripeElement {
  addEventListener: (
    type: string,
    listener: (event: { complete?: boolean; error?: { message: string } }) => void
  ) => void;

  mount(selector: string): void;
}

interface StripeElementsCreator {
  create(type: string, options: any): StripeElement;
}

export interface StripeInstance {
  elements(): StripeElementsCreator;

  confirmCardSetup(
    secret: string,
    data: any,
    config: any
  ): Promise<{
    error: { message: string; decline_code: string };
    setupIntent: any;
  }>;

  confirmCardPayment(
    secret: string,
    data: any,
    config: any
  ): Promise<{
    error: { message: string };
    paymentIntent: any;
  }>;

  retrieveSetupIntent(secret: string): Promise<{
    error: { message: string };
    setupIntent: any;
  }>;

  retrievePaymentIntent(secret: string): Promise<{
    error: { message: string };
    paymentIntent: any;
  }>;
}

interface StripeAddress {
  line1?: string;
  line2?: string;
  city?: string;
  state?: string;
  country?: string;
  postal_code?: string;
}

@Component({
  selector: 'orderform-payment-method-stripe-v2',
  templateUrl: './stripe-v2.component.html',
  styleUrls: ['./stripe-v2.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => StripeV2Component),
      multi: true,
    },
  ],
})
export class StripeV2Component
  extends AbstractMethodComponent
  implements OnInit, OnDestroy, ControlValueAccessor, AfterViewInit
{
  newWindow: Window | null = null;
  intervalId: number | null = null;
  @Input() config: InterfacePaymentProviderStipe;

  @Input() styles?: CreditCardStyles = CREDIT_CARD_DEFAULT_STYLES;

  public stripeError$ = {
    number: new BehaviorSubject<string>(''),
    expire: new BehaviorSubject<string>(''),
    cvc: new BehaviorSubject<string>(''),
  };
  private _stripeComplete = {
    number: false,
    expire: false,
    cvc: false,
  };
  private readonly _subscriptionBeforeSubmit = this._orderformFascade.beforeSubmit.subscribe(() =>
    this._onBeforeSubmit()
  );
  private readonly _subscriptionFormValidation = this._orderformFascade.formValidation.subscribe(
    () => this._onFormValidation(),
    this._element
  );
  private _settings: OrderformSettingsState;
  private readonly _settings$ = this._orderformFascade.settings$.subscribe((settings) => {
    this._settings = settings;
  });
  private _stripeInstance: StripeInstance;
  private _stripeCardNumber: StripeElement;
  private _stripeCardExpire: StripeElement;
  private _stripeCardCvc: StripeElement;
  private _stripeToken: { card: any };
  private _waitStripeInitialized: Promise<boolean>;
  private transaction: Transaction;

  constructor(
    private readonly _orderformFascade: OrderformFascade,
    private readonly _orderFormService: OrderformService,
    @Inject(LOCALE_ID) private readonly _locale: string,
    injector: Injector,
    private readonly http: HttpClient,
    private customErrorMessageService: CustomErrorMessageService,
    public externalResourcesLoader: ExternalResourcesLoaderService
  ) {
    super(injector);
  }

  getStripe(): (publicKey: string, config: { locale: string }) => StripeInstance {
    return (<any>window).Stripe;
  }

  ngOnInit(): void {
    this.transaction = this._monitoring.startTransaction('stripe-v2 credit card', 'stripe-v2');

    this._waitStripeInitialized = (async () => {
      if (!this.getStripe()) {
        await this.externalResourcesLoader.load(this._document, [this.config.apiUrl], [], true);
      }

      /**
       * get Stripe Function from asset load (_loadExternalResources)
       */
      const stripeFunction = this.getStripe();

      /**
       * Check stripe has loaded right
       */
      if (!stripeFunction) {
        console.log('Stripe not exists');
        return false;
      }

      /**
       * create instance
       */
      this._stripeInstance = stripeFunction(this.config.publicKey, {
        locale: this._locale.replace(/-[a-zA-Z0-9]{2,3}$/, ''),
      });

      const elements = this._stripeInstance.elements();
      const style = {
        base: {
          iconColor: this.styles.textColor,
          color: this.styles.textColor,
          fontSize: parseFloat(this.styles.fontSize) * 16 + 'px', // send as px; 1rem = 16px
          ':-webkit-autofill': {
            color: this.styles.placeholderColor,
          },
          '::placeholder': {
            color: this.styles.placeholderColor,
          },
        },
        invalid: {
          iconColor: this.styles.textColor,
          color: this.styles.textColor,
        },
      };

      const classes = {
        complete: 'ng-valid',
        invalid: 'ng-invalid',
      };

      this._stripeCardNumber = elements.create('cardNumber', {
        placeholder: '4444 3333 2222 1111',
        showIcon: true,
        style,
        classes,
      });

      this._stripeCardExpire = elements.create('cardExpiry', {
        placeholder: '03 / ' + ('' + (new Date().getFullYear() + 2)).slice(-2),
        style,
        classes,
      });

      this._stripeCardCvc = elements.create('cardCvc', {
        placeholder: '●●●',
        style: {
          ...style,
          base: {
            ...style.base,
            ':-webkit-autofill': {
              color: '#E6E9EC',
            },
            '::placeholder': {
              color: '#E6E9EC',
            },
          },
        },
        classes,
      });

      const fields = {
        number: this._stripeCardNumber,
        expire: this._stripeCardExpire,
        cvc: this._stripeCardCvc,
      };
      Object.keys(fields).forEach((field) => {
        fields[field].addEventListener('change', (event) => {
          this._stripeComplete[field] = event.complete;
          this._paymentDataChanged.next({ valid: this._isValid() });
          if (event.error) {
            this.stripeError$[field].next(event.error.message);
          } else {
            this.stripeError$[field].next('');
          }
        });
      });

      return true;
    })();
  }

  public async ngAfterViewInit() {
    if ((await this._waitStripeInitialized) === false) {
      return;
    }
    this._stripeCardNumber.mount('#stripe-card-number-' + this.uniqueInstanceId);
    this._stripeCardExpire.mount('#stripe-card-expire-' + this.uniqueInstanceId);
    this._stripeCardCvc.mount('#stripe-card-cvc-' + this.uniqueInstanceId);
  }

  createStripeIntent(order: OrderformOrderState): Promise<any> {
    const { protocol, hostname, port, href } = window.location;
    const baseUrl = `${protocol}//${hostname}${port ? `:${port}` : ''}`;
    const apiUrl = baseUrl + '/buy/stripe_v2/create_intent';
    const postData = {
      currency: order.order.currency,
      amount: order.order_summary?.order_final?.gross_1st ?? 0,
      email: order.order.shipping_address?.buyer_address_email ?? '',
      payment_plan_id: order.order.payment_plan_id,
      payment_provider_id: order.order.payment_provider.payment_provider_id,
      url: href,
    };

    try {
      this._monitoring.logData('[stripe-v2 frontend] create-intent postData', postData);
      this.transaction.addLabels({ '[stripe-v2 frontend] create-intent postData': JSON.stringify(postData) });
      return this.http.post(apiUrl, postData).toPromise();
    } catch (error) {
      console.error('Error creating payment intent:', error);
      throw error;
    }
  }

  async getOrder() {
    return firstValueFrom(this._orderformFascade.order$.pipe(map((order) => order)));
  }

  createPaymentMethod(order: any) {
    return {
      card: this._stripeCardNumber,
      billing_details: {
        address: this._mapToStripeAddress(
          order.order.has_billing_address === 0 ? order.order.shipping_address : order.order.billing_address
        ),
      },
    };
  }

  async handleSetupIntent(response: any, payment_method: any, order: OrderformOrderState): Promise<{ valid: boolean }> {
    let result;

    try {
      result = await this._stripeInstance.confirmCardSetup(
        response.intentClientSecret,
        { payment_method, return_url: window.location.href },
        {
          handleActions: true,
        }
      );
    } catch (error) {
      console.error('Failed to retrieve setupIntent', error);
      return { valid: false };
    }

    if (result.error) {
      console.log('Stripe Error:', result.error);
      const declinedErrorMessage = this.customErrorMessageService.getCustomErrorMessageFromOrderData(result, order);
      this._toaster.error(declinedErrorMessage);
      this._monitoring.trackValidationError(
        'exception:' + result.error.code + ':' + JSON.stringify(result.error),
        'frontend',
        'ds-orderform-payment-stripe-v2'
      );

      return { valid: false };
    } else {
      this._monitoring.logData('[stripe-v2 frontend] confirmCardSetup result', result);
      this.transaction.addLabels({ '[stripe-v2 frontend] confirmCardSetup result': JSON.stringify(result) });
      if (result.setupIntent.status === 'succeeded' || result.setupIntent.status === 'requires_capture') {
        this._stripeToken = result.setupIntent.id;
        this._paymentDataChanged.next({
          valid: true,
          stripe_token: result.setupIntent.id,
        });
        return { valid: true };
      } else {
        const declinedErrorMessage = this.customErrorMessageService.getCustomErrorMessageFromOrderData(result, order);
        this._toaster.error(declinedErrorMessage);
        return { valid: false };
      }
    }
  }

  async handlePaymentIntent(
    response: any,
    payment_method: any,
    order: OrderformOrderState
  ): Promise<{ valid: boolean }> {
    let result;
    try {
      result = await this._stripeInstance.confirmCardPayment(
        response.intentClientSecret,
        { payment_method, return_url: window.location.href },
        {
          handleActions: true,
        }
      );
    } catch (error) {
      console.error('Failed to retrieve payment intent:', error);
      return { valid: false };
    }

    if (result.error) {
      await firstValueFrom(this._orderformFascade.submitOrderForm());
      const updatedOrder = await this.getOrder();
      this._paymentDataChanged.next({
        valid: false,
      });

      const declinedErrorMessage = this.customErrorMessageService.getCustomErrorMessageFromOrderData(result, order);
      this._toaster.error(declinedErrorMessage);

      this._monitoring.trackValidationError('exception:' + result.error, 'frontend', 'ds-orderform-payment-stripe-v2');

      return { valid: false };
    } else {
      this._monitoring.logData('[stripe-v2 frontend] confirmCardPayment result', result);
      this.transaction.addLabels({ '[stripe-v2 frontend] confirmCardPayment result': JSON.stringify(result) });
      if (result.paymentIntent.status === 'succeeded' || result.paymentIntent.status === 'requires_capture') {
        this._stripeToken = result.paymentIntent.id;
        this._paymentDataChanged.next({
          valid: true,
          stripe_token: result.paymentIntent.id,
        });
        return { valid: true };
      } else {
        const declinedErrorMessage = this.customErrorMessageService.getCustomErrorMessageFromOrderData(result, order);
        this._toaster.error(declinedErrorMessage);
        return { valid: false };
      }
    }
  }

  private async _onBeforeSubmit(): Promise<{ valid: boolean }> {
    if (!this.isActive()) {
      return Promise.resolve({ valid: true });
    }
    if (this._stripeToken) {
      this._paymentDataChanged.next({
        valid: true,
        stripe_token: this._stripeToken,
      });
      return Promise.resolve({
        valid: true,
      });
    } else {
      this._paymentDataChanged.next({
        valid: false,
      });
    }

    if (!this._isValid()) {
      return Promise.resolve({ valid: false });
    }

    const order = await this.getOrder();

    const payment_method: { card: any; billing_details?: any } = this.createPaymentMethod(order);

    const response = await this.createStripeIntent(order);

    this._monitoring.logData('[stripe-v2 frontend] createStripeIntent response', response);
    if (response.type == 'setup_intent') {
      return this.handleSetupIntent(response, payment_method, order);
    }
    if (response.type == 'payment_intent') {
      return this.handlePaymentIntent(response, payment_method, order);
    }
  }

  private _mapToStripeAddress(addressInput: CustomFormData): StripeAddress {
    const stripeAddress: StripeAddress = {};
    if (!addressInput) {
      return stripeAddress;
    }

    const StripeAddressMapping: Partial<{
      [P in keyof StripeAddress]: string[];
    }> = {
      line1: ['buyer_address_street', 'buyer_address_street_billing'],
      line2: ['buyer_address_street2', 'buyer_address_street2_billing'],
      city: ['buyer_address_city', 'buyer_address_city_billing'],
      state: ['buyer_address_state', 'buyer_address_state_billing'],
      country: [
        'buyer_address_private_country',
        'buyer_address_private_country_billing',
        'country',
        'country_billing',
        'address_country',
        'address_country_billing',
        'buyer_address_country',
        'buyer_address_country_billing',
      ],
      postal_code: [
        'buyer_address_private_zipcode',
        'buyer_address_private_zipcode_billing',
        'buyer_address_zipcode',
        'buyer_address_zipcode_billing',
      ],
    };

    Object.keys(StripeAddressMapping).forEach((stripeKey) => {
      const addressInputKeys = StripeAddressMapping[stripeKey];
      const foundAddressInputKey = addressInputKeys.find((addressInputKey) =>
        addressInput.hasOwnProperty(addressInputKey)
      );
      if (foundAddressInputKey && addressInput[foundAddressInputKey]) {
        stripeAddress[stripeKey] = addressInput[foundAddressInputKey];
      }
    });

    return stripeAddress;
  }

  private _isValid() {
    const notCompletedFields = Object.keys(this._stripeComplete).filter((field) => !this._stripeComplete[field]);
    const notCompleted = notCompletedFields.length > 0;
    return !notCompleted;
  }

  ngOnDestroy() {
    this._subscriptionBeforeSubmit.unsubscribe();
    this._subscriptionFormValidation.unsubscribe();
    this._settings$.unsubscribe();
    this.transaction.end();
  }

  private _onFormValidation(): Promise<OrderFormValidationResult> {
    if (!this.isActive() || this._isValid()) {
      return Promise.resolve({
        valid: true,
        component: 'ds-orderform-payment-stripe',
        invalidFieldList: [],
      });
    }
    const notCompletedFields = Object.keys(this._stripeComplete).filter((field) => !this._stripeComplete[field]);
    // show generic error
    const invalidFieldList: { id: string; errors: string[] }[] = [];
    notCompletedFields.forEach((field) => {
      if (this.stripeError$[field].getValue() === '') {
        this.stripeError$[field].next(
          $localize`:@@payment_method.label.stripe_incomplete_generic:Die Eingabe wird benötigt.`
        );
        invalidFieldList.push({ id: field, errors: ['required'] });
      } else {
        invalidFieldList.push({
          id: field,
          errors: [this.stripeError$[field].getValue()],
        });
      }
    });
    return Promise.resolve({
      valid: false,
      component: 'ds-orderform-payment-stripe',
      invalidFieldList,
    });
  }
}
